import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase-config';

const RBACManager = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Sample data for RBAC system
  const sampleUsers = [
    {
      id: '1',
      name: 'Admin Péter',
      email: 'admin@hopphomes.com',
      role: 'super_admin',
      tenant: 'hopphomes',
      status: 'active',
      lastLogin: '2024-01-22T10:30:00Z',
      created: '2024-01-01T00:00:00Z',
      permissions: ['*']
    },
    {
      id: '2',
      name: 'Manager Anna',
      email: 'anna.manager@client1.com',
      role: 'tenant_admin',
      tenant: 'client1',
      status: 'active',
      lastLogin: '2024-01-21T15:45:00Z',
      created: '2024-01-10T00:00:00Z',
      permissions: ['contacts.read', 'contacts.write', 'pipelines.read', 'pipelines.write', 'automations.read']
    },
    {
      id: '3',
      name: 'Sales Gábor',
      email: 'gabor.sales@client1.com',
      role: 'sales_rep',
      tenant: 'client1',
      status: 'active',
      lastLogin: '2024-01-22T09:15:00Z',
      created: '2024-01-15T00:00:00Z',
      permissions: ['contacts.read', 'pipelines.read', 'pipelines.write']
    },
    {
      id: '4',
      name: 'Support Eszter',
      email: 'eszter.support@client2.com',
      role: 'support_agent',
      tenant: 'client2',
      status: 'inactive',
      lastLogin: '2024-01-20T14:20:00Z',
      created: '2024-01-12T00:00:00Z',
      permissions: ['conversations.read', 'conversations.write', 'contacts.read']
    }
  ];

  const sampleRoles = [
    {
      id: 'super_admin',
      name: 'Super Admin',
      description: 'Teljes rendszer hozzáférés minden tenanthoz',
      permissions: ['*'],
      level: 100,
      color: 'bg-red-500'
    },
    {
      id: 'tenant_admin',
      name: 'Tenant Admin',
      description: 'Teljes hozzáférés saját tenant-hez',
      permissions: [
        'contacts.*', 'pipelines.*', 'automations.*', 'conversations.*',
        'campaigns.*', 'forms.*', 'calendars.*', 'payments.*', 'reporting.*',
        'users.read', 'users.write'
      ],
      level: 80,
      color: 'bg-purple-500'
    },
    {
      id: 'sales_manager',
      name: 'Sales Manager',
      description: 'Értékesítési csapat vezetője',
      permissions: [
        'contacts.*', 'pipelines.*', 'automations.read', 'reporting.read',
        'users.read'
      ],
      level: 60,
      color: 'bg-blue-500'
    },
    {
      id: 'sales_rep',
      name: 'Sales Representative',
      description: 'Értékesítési munkatárs',
      permissions: [
        'contacts.read', 'contacts.write', 'pipelines.read', 'pipelines.write'
      ],
      level: 40,
      color: 'bg-green-500'
    },
    {
      id: 'support_agent',
      name: 'Support Agent',
      description: 'Ügyfélszolgálati munkatárs',
      permissions: [
        'conversations.*', 'contacts.read', 'contacts.write'
      ],
      level: 30,
      color: 'bg-yellow-500'
    },
    {
      id: 'viewer',
      name: 'Viewer',
      description: 'Csak olvasási jogosultság',
      permissions: [
        'contacts.read', 'pipelines.read', 'reporting.read'
      ],
      level: 10,
      color: 'bg-gray-500'
    }
  ];

  const sampleTenants = [
    {
      id: 'hopphomes',
      name: 'HoppHomes',
      domain: 'hopphomes.com',
      status: 'active',
      plan: 'enterprise',
      users: 1,
      created: '2024-01-01T00:00:00Z',
      settings: {
        maxUsers: 100,
        features: ['*']
      }
    },
    {
      id: 'client1',
      name: 'Tech Solutions Kft.',
      domain: 'techsolutions.hu',
      status: 'active',
      plan: 'professional',
      users: 2,
      created: '2024-01-10T00:00:00Z',
      settings: {
        maxUsers: 25,
        features: ['contacts', 'pipelines', 'automations', 'campaigns']
      }
    },
    {
      id: 'client2',
      name: 'Marketing Pro',
      domain: 'marketingpro.com',
      status: 'active',
      plan: 'standard',
      users: 1,
      created: '2024-01-12T00:00:00Z',
      settings: {
        maxUsers: 10,
        features: ['contacts', 'conversations', 'campaigns']
      }
    }
  ];

  const allPermissions = [
    { module: 'contacts', actions: ['read', 'write', 'delete'], description: 'Kontakt kezelés' },
    { module: 'pipelines', actions: ['read', 'write', 'delete'], description: 'Pipeline kezelés' },
    { module: 'automations', actions: ['read', 'write', 'delete'], description: 'Automatizáció kezelés' },
    { module: 'conversations', actions: ['read', 'write', 'delete'], description: 'Beszélgetés kezelés' },
    { module: 'campaigns', actions: ['read', 'write', 'delete'], description: 'Kampány kezelés' },
    { module: 'forms', actions: ['read', 'write', 'delete'], description: 'Űrlap kezelés' },
    { module: 'calendars', actions: ['read', 'write', 'delete'], description: 'Naptár kezelés' },
    { module: 'payments', actions: ['read', 'write', 'delete'], description: 'Fizetés kezelés' },
    { module: 'reporting', actions: ['read', 'write'], description: 'Jelentés kezelés' },
    { module: 'users', actions: ['read', 'write', 'delete'], description: 'Felhasználó kezelés' },
    { module: 'settings', actions: ['read', 'write'], description: 'Beállítások' }
  ];

  useEffect(() => {
    setUsers(sampleUsers);
    setRoles(sampleRoles);
    setTenants(sampleTenants);
    setPermissions(allPermissions);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'inactive': return 'bg-red-500';
      case 'pending': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getRoleColor = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.color : 'bg-gray-500';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const UsersTab = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">Felhasználók</h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Új Felhasználó</span>
        </button>
      </div>

      <div className="bg-[#2a2a2a] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a1a1a]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Felhasználó</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Szerepkör</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tenant</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Státusz</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Utolsó belépés</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Műveletek</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-[#3a3a3a] transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-white font-medium">{user.name}</div>
                      <div className="text-gray-400 text-sm">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`${getRoleColor(user.role)} text-white text-xs px-3 py-1 rounded-full`}>
                      {roles.find(r => r.id === user.role)?.name || user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-300">{tenants.find(t => t.id === user.tenant)?.name || user.tenant}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(user.status)}`}></div>
                      <span className="text-gray-300 capitalize">{user.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-400 text-sm">{formatDate(user.lastLogin)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-blue-400 hover:text-blue-300 text-sm"
                      >
                        Szerkesztés
                      </button>
                      <button className="text-red-400 hover:text-red-300 text-sm">
                        Törlés
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const RolesTab = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">Szerepkörök</h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Új Szerepkör</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <div
            key={role.id}
            className="bg-[#2a2a2a] rounded-xl p-6 hover:bg-[#3a3a3a] transition-all duration-200 cursor-pointer border border-white/5 hover:border-white/10"
            onClick={() => setSelectedRole(role)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`${role.color} rounded-lg p-3 text-white`}>
                <div className="text-2xl">👤</div>
              </div>
              <div className="text-right">
                <div className="text-white font-bold">Level {role.level}</div>
                <div className="text-gray-400 text-sm">{users.filter(u => u.role === role.id).length} felhasználó</div>
              </div>
            </div>

            <h4 className="text-white font-bold text-lg mb-2">{role.name}</h4>
            <p className="text-gray-400 text-sm mb-4">{role.description}</p>

            <div className="space-y-2">
              <div className="text-white font-medium text-sm">Jogosultságok:</div>
              <div className="flex flex-wrap gap-1">
                {role.permissions.slice(0, 3).map((permission, index) => (
                  <span
                    key={index}
                    className="bg-white/10 text-white text-xs px-2 py-1 rounded-full"
                  >
                    {permission}
                  </span>
                ))}
                {role.permissions.length > 3 && (
                  <span className="bg-white/10 text-white text-xs px-2 py-1 rounded-full">
                    +{role.permissions.length - 3} további
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const TenantsTab = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">Tenant-ek</h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Új Tenant</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tenants.map((tenant) => (
          <div
            key={tenant.id}
            className="bg-[#2a2a2a] rounded-xl p-6 hover:bg-[#3a3a3a] transition-all duration-200 border border-white/5 hover:border-white/10"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-3 text-white">
                <div className="text-2xl">🏢</div>
              </div>
              <div className={`w-3 h-3 rounded-full ${getStatusColor(tenant.status)}`}></div>
            </div>

            <h4 className="text-white font-bold text-lg mb-1">{tenant.name}</h4>
            <p className="text-gray-400 text-sm mb-4">{tenant.domain}</p>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{tenant.users}</div>
                <div className="text-xs text-gray-400">Felhasználók</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-blue-400 capitalize">{tenant.plan}</div>
                <div className="text-xs text-gray-400">Csomag</div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-white font-medium text-sm">Funkciók:</div>
              <div className="flex flex-wrap gap-1">
                {tenant.settings.features.slice(0, 3).map((feature, index) => (
                  <span
                    key={index}
                    className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded-full"
                  >
                    {feature}
                  </span>
                ))}
                {tenant.settings.features.length > 3 && (
                  <span className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded-full">
                    +{tenant.settings.features.length - 3}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-gray-400 text-xs">
                Létrehozva: {formatDate(tenant.created)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const PermissionsTab = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">Jogosultságok</h3>
      </div>

      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {permissions.map((module) => (
            <div key={module.module} className="bg-[#1a1a1a] rounded-lg p-4">
              <h4 className="text-white font-bold text-lg mb-2 capitalize">{module.module}</h4>
              <p className="text-gray-400 text-sm mb-4">{module.description}</p>
              
              <div className="space-y-2">
                <div className="text-white font-medium text-sm">Elérhető műveletek:</div>
                <div className="flex flex-wrap gap-2">
                  {module.actions.map((action) => (
                    <span
                      key={action}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs px-3 py-1 rounded-full"
                    >
                      {module.module}.{action}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <span className="text-3xl mr-3">🔐</span>
          RBAC Kezelő
        </h2>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-[#2a2a2a] rounded-lg p-1">
        {[
          { id: 'users', label: 'Felhasználók', icon: '👥' },
          { id: 'roles', label: 'Szerepkörök', icon: '👤' },
          { id: 'tenants', label: 'Tenant-ek', icon: '🏢' },
          { id: 'permissions', label: 'Jogosultságok', icon: '🔑' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a]'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-6">
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'roles' && <RolesTab />}
        {activeTab === 'tenants' && <TenantsTab />}
        {activeTab === 'permissions' && <PermissionsTab />}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-xl border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedUser.name}</h2>
                  <p className="text-gray-400">{selectedUser.email}</p>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-white font-bold mb-2">Alapadatok</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-gray-300">
                        <span>Szerepkör:</span>
                        <span className={`${getRoleColor(selectedUser.role)} text-white text-xs px-2 py-1 rounded-full`}>
                          {roles.find(r => r.id === selectedUser.role)?.name}
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Tenant:</span>
                        <span>{tenants.find(t => t.id === selectedUser.tenant)?.name}</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Státusz:</span>
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${getStatusColor(selectedUser.status)}`}></div>
                          <span className="capitalize">{selectedUser.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-white font-bold mb-2">Aktivitás</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-gray-300">
                        <span>Utolsó belépés:</span>
                        <span>{formatDate(selectedUser.lastLogin)}</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Regisztráció:</span>
                        <span>{formatDate(selectedUser.created)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-white font-bold mb-3">Jogosultságok</h3>
                <div className="bg-[#2a2a2a] rounded-lg p-4">
                  <div className="flex flex-wrap gap-2">
                    {selectedUser.permissions.map((permission, index) => (
                      <span
                        key={index}
                        className="bg-blue-500/20 text-blue-300 text-sm px-3 py-1 rounded-full"
                      >
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setSelectedUser(null)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Bezárás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RBACManager;
